import { Alert, Button, MobileDrawer } from "@jugl-web/ui-kit";
import { useAppVariant, useTranslations } from "@jugl-web/utils";
import { FC, ReactNode } from "react";
import { Lottie } from "@jugl-web/utils/utils/Lottie";
import readOnlyAnimation from "./assets/read-only-animation.json";

interface WriteRestrictedAlertProps {
  isOpen: boolean;
  isAdmin: boolean;
  onRequestClose: () => void;
  onRequestPlanUpgrade?: (closeAlert: () => void) => void;
}

export const WriteRestrictedAlert: FC<WriteRestrictedAlertProps> = ({
  isOpen,
  isAdmin,
  onRequestClose,
  onRequestPlanUpgrade,
}) => {
  const { t } = useTranslations();
  const { isMobile } = useAppVariant();

  const canUpgradePlan = isAdmin && !!onRequestPlanUpgrade;

  const copy = {
    header: t({
      id: "write-restricted-alert-component.header",
      defaultMessage: "Workspace Subscription",
    }),
    title: t({
      id: "write-restricted-alert-component.title",
      defaultMessage: "No actions allowed",
    }),
    readOnlyMessage: t({
      id: "write-restricted-alert-component.read-only-message",
      defaultMessage:
        "You cannot update anything because the workspace is in read-only mode.",
    }),
    upgradeSubscriptionMessageAdmin: t(
      {
        id: "write-restricted-alert-component.upgrade-subscription-message-admin",
        defaultMessage:
          "To enable edit mode, please <b>upgrade the workspace subscription.</b>",
      },
      {
        b: (chunks: ReactNode) => (
          <span className="font-bold text-primary-900">{chunks}</span>
        ),
      }
    ),
    upgradeSubscriptionMessageMember: t(
      {
        id: "write-restricted-alert-component.upgrade-subscription-message-member",
        defaultMessage:
          "To enable edit mode, please contact the workspace admin to <b>upgrade the subscription.</b>",
      },
      {
        b: (chunks: ReactNode) => (
          <span className="font-bold text-primary-900">{chunks}</span>
        ),
      }
    ),
  };

  if (isMobile) {
    return (
      <MobileDrawer isOpen={isOpen} onClose={onRequestClose}>
        <MobileDrawer.Content className="flex flex-col items-center">
          <div className="my-8">
            <Lottie
              options={{ animationData: readOnlyAnimation }}
              width={250}
            />
          </div>
          <span className="mb-4 text-xl leading-5 font-medium tracking-[0.2px] text-dark">
            {copy.title}
          </span>
          <span className="mb-12 text-center text-base leading-[22px] font-normal tracking-[0.16px] text-dark-800">
            {copy.readOnlyMessage}
            {isAdmin ? (
              <span>{copy.upgradeSubscriptionMessageAdmin}</span>
            ) : (
              <span>{copy.upgradeSubscriptionMessageMember}</span>
            )}
          </span>
          <Button fullWidth className="h-10 shrink-0" onClick={onRequestClose}>
            {t({
              id: "common.okay",
              defaultMessage: "Okay",
            })}
          </Button>
        </MobileDrawer.Content>
      </MobileDrawer>
    );
  }

  return (
    <Alert
      isOpen={isOpen}
      isCloseButtonVisible
      onRequestClose={onRequestClose}
      header={copy.header}
      title={copy.title}
      img={
        <Lottie options={{ animationData: readOnlyAnimation }} width={250} />
      }
      buttons={
        canUpgradePlan
          ? [
              {
                text: t({
                  id: "write-restricted-alert-component.upgrade-plan-button-text",
                  defaultMessage: "Upgrade plan",
                }),
                onClick: (_, { closeAlert }) =>
                  onRequestPlanUpgrade(closeAlert),
              },
            ]
          : undefined
      }
      content={
        <span>
          {copy.readOnlyMessage}
          {isAdmin ? (
            <span>{copy.upgradeSubscriptionMessageAdmin}</span>
          ) : (
            <span>{copy.upgradeSubscriptionMessageMember}</span>
          )}
        </span>
      }
    />
  );
};
